import { useEffect, useState } from 'react';
import {
  api,
  type LabOrderRow,
  type PatientSummary,
  type WhatsAppDraft,
} from '../api';
import { useI18n } from '../i18n';
import { PatientPicker } from '../components/PatientPicker';
import { EmptyState, ErrorState, Field, Modal, Skeleton, Spinner, Tabs, WhatsAppDialog } from '../components';
import { formatMinor, minorToInput, parseToMinor } from '../money';
import { describe } from '../errors';
import { ConfirmRemove } from '../components/ConfirmRemove';
import { EditContact } from '../components/EditContact';
import { RecordPayment, type PayTo } from './Finance';

export function Laboratory({
  clinicId,
  canWrite,
  permissions,
  currency,
}: {
  clinicId: string;
  canWrite: boolean;
  permissions: string[];
  currency: string;
}) {
  const { t } = useI18n();
  const [tab, setTab] = useState<'orders' | 'laboratories'>('orders');
  const can = (p: string) => permissions.includes(p);
  const canPay = can('finance.payment.record');

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t.lab.title}</h1>
        </div>
      </div>
      <Tabs
        value={tab}
        onChange={(v) => setTab(v as 'orders' | 'laboratories')}
        tabs={[
          { value: 'orders', label: t.lab.orders },
          { value: 'laboratories', label: t.lab.laboratories },
        ]}
      />
      {tab === 'orders' ? (
        <Orders clinicId={clinicId} canWrite={canWrite} canDelete={can('lab.order.delete')} currency={currency} />
      ) : (
        <Labs clinicId={clinicId} canWrite={canWrite} canPay={canPay} currency={currency} />
      )}
    </>
  );
}

function Orders({
  clinicId,
  canWrite,
  canDelete,
  currency,
}: {
  clinicId: string;
  canWrite: boolean;
  canDelete: boolean;
  currency: string;
}) {
  const { t, locale, formatDate, personName } = useI18n();
  const [rows, setRows] = useState<LabOrderRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<WhatsAppDraft | null>(null);
  const [nonce, setNonce] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [removing, setRemoving] = useState<LabOrderRow | null>(null);
  const [editing, setEditing] = useState<LabOrderRow | null>(null);
  const [correcting, setCorrecting] = useState<LabOrderRow | null>(null);

  useEffect(() => {
    setRows(null);
    api.labOrders().then((r) => setRows(r.orders)).catch((e) => setError(describe(e, t)));
  }, [clinicId, nonce, t]);

  /**
   * Move an order to a named state.
   *
   * It used to be a button called "Advance →" that silently took the first
   * allowed transition. Pressing it appeared to do nothing: the label never
   * said where it was going, and the only visible change was a small badge
   * further along the same row. Now the button says the state it moves to, and
   * where more than one is allowed they are all offered.
   */
  async function move(order: LabOrderRow, next: string) {
    setBusyId(order.id);
    setError(null);
    try {
      await api.setLabStatus(order.id, { status: next });
      setNonce((n) => n + 1);
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusyId(null);
    }
  }

  async function whatsapp(order: LabOrderRow, reminder: boolean) {
    setBusyId(order.id);
    try {
      const r = await api.labWhatsApp(order.id, { locale, reminder });
      setDraft(r.draft);
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusyId(null);
    }
  }

  if (error) return <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />;

  return (
    <>
      <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 12 }}>
        {canWrite ? (
          <button type="button" className="btn" onClick={() => setCreating(true)}>
            + {t.lab.newOrder}
          </button>
        ) : null}
      </div>

      <div className="notice" style={{ marginBottom: 12 }}>
        <span>{t.lab.costNote}</span>
      </div>

      <div className="card">
        {rows === null ? (
          <Skeleton rows={5} />
        ) : rows.length === 0 ? (
          <EmptyState title={t.lab.noOrders} />
        ) : (
          <div className="card-body flush table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t.finance.patient}</th>
                  <th>{t.lab.item}</th>
                  <th>{t.lab.teeth}</th>
                  <th>{t.lab.due}</th>
                  <th>{t.lab.labCost}</th>
                  <th>{t.finance.status}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.id}>
                    <td>
                      <strong>{personName(o.patient)}</strong>
                      <span style={{ display: 'block', color: 'var(--ink-3)', fontSize: 12 }}>
                        {o.laboratory.name}
                      </span>
                    </td>
                    <td>
                      {o.itemType}
                      {o.shade ? <span style={{ color: 'var(--ink-3)' }}> · {o.shade}</span> : null}
                    </td>
                    <td className="ltr-value">{o.teeth.join(', ') || '—'}</td>
                    <td>
                      {o.dueOn ? formatDate(o.dueOn) : '—'}
                      {/* Overdue work is flagged, not left to be noticed. */}
                      {o.overdue ? <span className="badge danger" style={{ marginInlineStart: 6 }}>{t.lab.overdue}</span> : null}
                    </td>
                    <td className="ltr-value">{formatMinor(o.labCostMinor, o.currency, locale)}</td>
                    <td>
                      <span className="badge info">
                        {(t.lab.states as Record<string, string>)[o.status] ?? o.status}
                      </span>
                      {/* Whether the clinic currently owes the lab for this
                          order — the money fact behind the status. */}
                      {o.payableRecorded ? (
                        <span className="badge warn" style={{ marginInlineStart: 6 }}>{t.lab.payableRecorded}</span>
                      ) : o.status !== 'draft' && o.status !== 'cancelled' && o.labCostMinor > 0 ? (
                        <span className="badge" style={{ marginInlineStart: 6 }}>{t.lab.pendingAtLab}</span>
                      ) : null}
                    </td>
                    <td style={{ textAlign: 'end' }}>
                      <div className="row" style={{ justifyContent: 'flex-end', gap: 6, flexWrap: 'wrap' }}>
                        {/* Each allowed transition, named. A dead-ended order
                            says so rather than showing a button that does
                            nothing. */}
                        {canWrite && o.allowedNext.length > 0
                          ? o.allowedNext.map((next, i) => (
                              <button
                                key={next}
                                type="button"
                                className={i === 0 ? 'btn btn-sm' : 'btn btn-ghost btn-sm'}
                                onClick={() => void move(o, next)}
                                disabled={busyId === o.id}
                              >
                                {busyId === o.id ? (
                                  <Spinner />
                                ) : (
                                  (t.lab.states as Record<string, string>)[next] ?? next
                                )}
                              </button>
                            ))
                          : null}
                        {o.laboratory.whatsappPhone ? (
                          <button
                            type="button"
                            className="btn-link"
                            onClick={() => void whatsapp(o, o.status !== 'draft')}
                          >
                            {t.lab.whatsapp}
                          </button>
                        ) : null}
                        {canWrite ? (
                          <>
                            <button type="button" className="btn-link" onClick={() => setEditing(o)}>
                              {t.remove.edit}
                            </button>
                            <button type="button" className="btn-link" onClick={() => setCorrecting(o)}>
                              {t.lab.correctStatus}
                            </button>
                          </>
                        ) : null}
                        {/* Only while it is still a draft. Once the laboratory
                            has it there is work and a cost attached, and the
                            honest end for it is `cancelled`. */}
                        {canDelete && o.status === 'draft' ? (
                          <button type="button" className="btn-link danger" onClick={() => setRemoving(o)}>
                            {t.remove.delete}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating ? (
        <NewOrder currency={currency}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {editing ? (
        <EditOrder
          order={editing}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {correcting ? (
        <CorrectStatus
          order={correcting}
          onClose={() => setCorrecting(null)}
          onDone={() => {
            setCorrecting(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {removing ? (
        <ConfirmRemove
          title={t.remove.deleteLabOrder}
          what={`${personName(removing.patient)} · ${removing.itemType}`}
          body={<p style={{ margin: 0, color: 'var(--ink-3)' }}>{t.remove.deleteOrderBody}</p>}
          actionLabel={t.remove.delete}
          onConfirm={() => api.deleteLabOrder(removing.id)}
          onClose={() => setRemoving(null)}
          onDone={() => {
            setRemoving(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {draft ? <WhatsAppDialog draft={draft} onClose={() => setDraft(null)} /> : null}
    </>
  );
}

function NewOrder({ currency, onClose, onCreated }: { currency: string; onClose: () => void; onCreated: () => void }) {
  const { t } = useI18n();
  const [, setPatients] = useState<PatientSummary[]>([]);
  const [labs, setLabs] = useState<Awaited<ReturnType<typeof api.laboratories>>['laboratories']>([]);
  const [form, setForm] = useState({
    patientId: '',
    laboratoryId: '',
    itemType: '',
    teeth: '',
    shade: '',
    dueOn: '',
    labCost: '',
    patientCharge: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void Promise.all([api.patients({ limit: 100 }), api.laboratories()]).then(([p, l]) => {
      setPatients(p.patients);
      setLabs(l.laboratories);
      setForm((f) => ({
        ...f,
        patientId: p.patients[0]?.id ?? '',
        laboratoryId: l.laboratories[0]?.id ?? '',
      }));
    });
  }, []);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.createLabOrder({
        patientId: form.patientId,
        laboratoryId: form.laboratoryId,
        itemType: form.itemType,
        teeth: form.teeth.split(',').map((x) => x.trim()).filter(Boolean),
        shade: form.shade || undefined,
        dueOn: form.dueOn || null,
        labCostMinor: parseToMinor(form.labCost, currency) ?? 0,
        patientChargeMinor: parseToMinor(form.patientCharge, currency) ?? 0,
      });
      onCreated();
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t.lab.newOrder}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button type="button" className="btn" onClick={() => void submit()} disabled={busy || !form.itemType || !form.laboratoryId}>
            {busy ? <><Spinner /> {t.patients.saving}</> : t.chart.save}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? <div className="notice danger" role="alert">{error}</div> : null}

        <div className="grid-2">
          <Field label={t.finance.patient} required>
            <PatientPicker value={form.patientId} onChange={(id) => setForm({ ...form, patientId: id })} />
          </Field>
          <Field label={t.lab.laboratories} required>
            <select value={form.laboratoryId} onChange={(e) => setForm({ ...form, laboratoryId: e.target.value })}>
              {labs.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </Field>
        </div>

        <Field label={t.lab.item} required>
          <input value={form.itemType} onChange={(e) => setForm({ ...form, itemType: e.target.value })} />
        </Field>

        <div className="grid-3">
          <Field label={t.lab.teeth} hint="16, 17">
            <input value={form.teeth} onChange={(e) => setForm({ ...form, teeth: e.target.value })} className="ltr-value" />
          </Field>
          <Field label={t.lab.shade}>
            <input value={form.shade} onChange={(e) => setForm({ ...form, shade: e.target.value })} className="ltr-value" />
          </Field>
          <Field label={t.lab.due}>
            <input type="date" value={form.dueOn} onChange={(e) => setForm({ ...form, dueOn: e.target.value })} className="ltr-value" />
          </Field>
        </div>

        {/* Two separate amounts, and the interface says why. */}
        <div className="grid-2">
          <Field label={t.lab.labCost} hint={t.lab.costNote}>
            <input value={form.labCost} onChange={(e) => setForm({ ...form, labCost: e.target.value })} className="ltr-value" inputMode="decimal" />
          </Field>
          <Field label={t.lab.patientCharge}>
            <input value={form.patientCharge} onChange={(e) => setForm({ ...form, patientCharge: e.target.value })} className="ltr-value" inputMode="decimal" />
          </Field>
        </div>
      </div>
    </Modal>
  );
}

type LabRow = Awaited<ReturnType<typeof api.laboratories>>['laboratories'][number];

function Labs({
  clinicId,
  canWrite,
  canPay,
  currency,
}: {
  clinicId: string;
  canWrite: boolean;
  canPay: boolean;
  currency: string;
}) {
  const { t, locale } = useI18n();
  const [rows, setRows] = useState<LabRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<LabRow | null>(null);
  const [removingLab, setRemovingLab] = useState<LabRow | null>(null);
  const [paying, setPaying] = useState<PayTo | null>(null);
  const [form, setForm] = useState({ name: '', phone: '', whatsappPhone: '', address: '' });
  const [nonce, setNonce] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setRows(null);
    api.laboratories().then((r) => setRows(r.laboratories)).catch((e) => setError(describe(e, t)));
  }, [clinicId, nonce, t]);

  async function submit() {
    setBusy(true);
    try {
      await api.createLaboratory(form);
      setAdding(false);
      setForm({ name: '', phone: '', whatsappPhone: '', address: '' });
      setNonce((n) => n + 1);
    } catch (e) {
      setError(describe(e, t));
    } finally {
      setBusy(false);
    }
  }

  if (error) return <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} />;

  return (
    <>
      <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 12 }}>
        {canWrite ? (
          <button type="button" className="btn" onClick={() => setAdding(true)}>
            + {t.lab.newLab}
          </button>
        ) : null}
      </div>

      <div className="card">
        {rows === null ? (
          <Skeleton rows={4} />
        ) : rows.length === 0 ? (
          <EmptyState title={t.lab.noLabs} />
        ) : (
          <div className="card-body flush table-scroll">
            <table>
              <thead>
                <tr>
                  <th>{t.inventory.name}</th>
                  <th>{t.patients.phone}</th>
                  <th>{t.finance.billed}</th>
                  <th>{t.finance.paid}</th>
                  <th>{t.lab.outstanding}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((l) => (
                  <tr key={l.id} style={{ opacity: l.active ? 1 : 0.55 }}>
                    <td>
                      <strong>{l.name}</strong>
                      {l.active ? null : (
                        <span className="badge" style={{ marginInlineStart: 8 }}>
                          {t.remove.retired}
                        </span>
                      )}
                    </td>
                    <td className="ltr-value">{l.whatsappPhone ?? l.phone ?? '—'}</td>
                    <td className="ltr-value">{formatMinor(l.billedMinor, currency, locale)}</td>
                    <td className="ltr-value">{formatMinor(l.paidMinor, currency, locale)}</td>
                    <td className="ltr-value">
                      <strong style={{ color: l.outstandingMinor > 0 ? 'var(--warn)' : 'var(--ok)' }}>
                        {formatMinor(l.outstandingMinor, currency, locale)}
                      </strong>
                      {l.pendingMinor > 0 ? (
                        <span style={{ display: 'block', color: 'var(--ink-3)', fontSize: 12 }}>
                          {t.lab.pendingAtLab}: {formatMinor(l.pendingMinor, currency, locale)}
                        </span>
                      ) : null}
                    </td>
                    <td style={{ textAlign: 'end', whiteSpace: 'nowrap' }}>
                      {/* Settle what is owed from the row that shows it. */}
                      {canPay && l.outstandingMinor > 0 ? (
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{ marginInlineEnd: 14 }}
                          onClick={() =>
                            setPaying({
                              kind: 'laboratory',
                              id: l.id,
                              name: l.name,
                              outstandingMinor: l.outstandingMinor,
                              currency,
                            })
                          }
                        >
                          {t.finance.pay}
                        </button>
                      ) : null}
                      {canWrite ? (
                        <>
                          <button
                            type="button"
                            className="btn-link"
                            style={{ marginInlineEnd: 14 }}
                            onClick={() => setEditing(l)}
                          >
                            {t.remove.edit}
                          </button>
                          {l.active ? (
                            <button
                              type="button"
                              className="btn-link danger"
                              onClick={() => setRemovingLab(l)}
                            >
                              {t.remove.confirm}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn-link"
                              onClick={async () => {
                                await api.updateLaboratory(l.id, { active: true });
                                setNonce((n) => n + 1);
                              }}
                            >
                              {t.remove.reactivate}
                            </button>
                          )}
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {paying ? (
        <RecordPayment
          payTo={paying}
          onClose={() => setPaying(null)}
          onDone={() => {
            setPaying(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {editing ? (
        <EditContact
          title={t.remove.editLaboratory}
          initial={editing}
          onSave={(patch) => api.updateLaboratory(editing.id, patch)}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {removingLab ? (
        <ConfirmRemove
          title={t.remove.deleteLaboratory}
          what={removingLab.name}
          onConfirm={() => api.deleteLaboratory(removingLab.id)}
          onClose={() => setRemovingLab(null)}
          onDone={() => {
            setRemovingLab(null);
            setNonce((n) => n + 1);
          }}
        />
      ) : null}

      {adding ? (
        <Modal
          title={t.lab.newLab}
          onClose={() => setAdding(false)}
          footer={
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setAdding(false)}>
                {t.patients.cancel}
              </button>
              <button type="button" className="btn" onClick={() => void submit()} disabled={busy || !form.name}>
                {busy ? <Spinner /> : t.chart.save}
              </button>
            </>
          }
        >
          <div className="stack">
            <Field label={t.inventory.name} required>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <div className="grid-2">
              <Field label={t.patients.phone}>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="ltr-value" />
              </Field>
              <Field label={t.patients.whatsapp}>
                <input value={form.whatsappPhone} onChange={(e) => setForm({ ...form, whatsappPhone: e.target.value })} className="ltr-value" />
              </Field>
            </div>
            <Field label={t.settings.address}>
              <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </Field>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

/**
 * Correct an order after the fact.
 *
 * The workflow buttons only go forward. This goes anywhere, because the
 * reason it exists is a wrong press — "collected" when the courier has not
 * been, "received" before the box arrived. It asks why, keeps the answer in
 * the order's history, and the money follows: undoing a wrong "received"
 * withdraws what the clinic had been recorded as owing the lab.
 */
function CorrectStatus({ order, onClose, onDone }: { order: LabOrderRow; onClose: () => void; onDone: () => void }) {
  const { t } = useI18n();
  const [status, setStatus] = useState(order.status);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Modal
      title={t.lab.correctStatus}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || status === order.status || note.trim().length < 3}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.setLabStatus(order.id, { status, note: note.trim(), correction: true });
                onDone();
              } catch (e) {
                setError(describe(e, t));
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? <Spinner /> : t.chart.save}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? <div className="notice danger" role="alert">{error}</div> : null}
        <p style={{ margin: 0, color: 'var(--ink-3)', fontSize: 13 }}>{t.lab.correctStatusHint}</p>
        <Field label={t.common.status} required>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            {order.allStates.map((st) => (
              <option key={st} value={st}>
                {(t.lab.states as Record<string, string>)[st] ?? st}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t.lab.correctionReason} required>
          <input value={note} onChange={(e) => setNote(e.target.value)} autoFocus />
        </Field>
      </div>
    </Modal>
  );
}

/**
 * Edit an order. The same fields as creating one; the price is the one that
 * matters most, because it is the one most often left blank at the desk and
 * filled in when the lab's invoice arrives.
 */
function EditOrder({ order, onClose, onDone }: { order: LabOrderRow; onClose: () => void; onDone: () => void }) {
  const { t } = useI18n();
  const [labs, setLabs] = useState<LabRow[]>([]);
  const initial = {
    laboratoryId: order.laboratoryId,
    itemType: order.itemType,
    teeth: order.teeth.join(', '),
    shade: order.shade ?? '',
    specification: order.specification ?? '',
    instructions: order.instructions ?? '',
    dueOn: order.dueOn ? String(order.dueOn).slice(0, 10) : '',
    labCost: minorToInput(order.labCostMinor, order.currency),
    patientCharge: minorToInput(order.patientChargeMinor, order.currency),
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.laboratories().then((r) => setLabs(r.laboratories));
  }, []);

  const patch: Record<string, unknown> = {};
  if (form.laboratoryId !== initial.laboratoryId) patch.laboratoryId = form.laboratoryId;
  if (form.itemType.trim() !== initial.itemType) patch.itemType = form.itemType.trim();
  if (form.teeth !== initial.teeth) patch.teeth = form.teeth.split(',').map((x) => x.trim()).filter(Boolean);
  if (form.shade !== initial.shade) patch.shade = form.shade || null;
  if (form.specification !== initial.specification) patch.specification = form.specification || null;
  if (form.instructions !== initial.instructions) patch.instructions = form.instructions || null;
  if (form.dueOn !== initial.dueOn) patch.dueOn = form.dueOn || null;
  const labCost = parseToMinor(form.labCost, order.currency);
  if (labCost !== null && labCost !== order.labCostMinor) patch.labCostMinor = labCost;
  const charge = parseToMinor(form.patientCharge, order.currency);
  if (charge !== null && charge !== order.patientChargeMinor) patch.patientChargeMinor = charge;

  return (
    <Modal
      title={t.lab.editOrder}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            {t.patients.cancel}
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || Object.keys(patch).length === 0 || !form.itemType.trim()}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.updateLabOrder(order.id, patch);
                onDone();
              } catch (e) {
                setError(describe(e, t));
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? <Spinner /> : t.chart.save}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? <div className="notice danger" role="alert">{error}</div> : null}

        <Field label={t.lab.laboratories} required hint={order.status !== 'draft' ? t.remove.cannotUndo : undefined}>
          <select
            value={form.laboratoryId}
            disabled={order.status !== 'draft'}
            onChange={(e) => setForm({ ...form, laboratoryId: e.target.value })}
          >
            {labs.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        </Field>

        <Field label={t.lab.item} required>
          <input value={form.itemType} onChange={(e) => setForm({ ...form, itemType: e.target.value })} />
        </Field>

        <div className="grid-3">
          <Field label={t.lab.teeth} hint="16, 17">
            <input value={form.teeth} onChange={(e) => setForm({ ...form, teeth: e.target.value })} className="ltr-value" />
          </Field>
          <Field label={t.lab.shade}>
            <input value={form.shade} onChange={(e) => setForm({ ...form, shade: e.target.value })} className="ltr-value" />
          </Field>
          <Field label={t.lab.due}>
            <input type="date" value={form.dueOn} onChange={(e) => setForm({ ...form, dueOn: e.target.value })} className="ltr-value" />
          </Field>
        </div>

        <div className="grid-2">
          <Field label={t.lab.labCost} hint={order.payableRecorded ? t.lab.labCostEditNote : t.lab.costNote}>
            <input value={form.labCost} onChange={(e) => setForm({ ...form, labCost: e.target.value })} className="ltr-value" inputMode="decimal" />
          </Field>
          <Field label={t.lab.patientCharge}>
            <input value={form.patientCharge} onChange={(e) => setForm({ ...form, patientCharge: e.target.value })} className="ltr-value" inputMode="decimal" />
          </Field>
        </div>

        <Field label={t.patients.notes} hint={t.patients.optional}>
          <textarea value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} rows={3} />
        </Field>
      </div>
    </Modal>
  );
}
